import { ContractHistoryEvent } from '@/connect/gen/modules/apiengine/services/contracts/contracts_pb';
import Decimal from 'decimal.js';
import { cloneDeep } from 'lodash';
import { Side } from '@/connect/gen/consts/commonconsts_pb';
import { Benchmark } from '@/connect/gen/consts/benchmarkconsts_pb';
import { ContractHistoryEventType } from '@/connect/gen/consts/loanhistoryconsts_pb';
import { useCompaniesStore } from '@/store/companies-store';

const zero = new Decimal(0);

export interface Agg {
  sum: AggregateSum;
  avg: AggregateAvg;
  noun: AggregateNoun;
  selected: 'avg' | 'sum';
  nounOrQuantity: (name: string) => string;
}

interface AggregateSum {
  // total/avg quantities
  openQuantity: bigint;
  contractValue: Decimal;
  contractValueAdjustment: Decimal;
  interestDueAmount: Decimal;
  unitPrice: Decimal;
}

interface AggregateAvg extends AggregateSum {
  // weighted avg rates
  rate: Decimal;
  rateModifiers: Benchmark[];
}

interface AggregateNoun {
  sides: Array<'LOAN' | 'BORROW'>;
  parties: string[];
  settlementType: string[];
  ticker: string[];
  cusip: string[];
  eventTypes: ContractHistoryEventType[];
  initiators: string[];
}

const initialSum: AggregateSum = {
  openQuantity: 0n,
  contractValue: zero,
  contractValueAdjustment: zero,
  interestDueAmount: zero,
  unitPrice: zero,
};

const initialAvg: AggregateAvg = {
  ...initialSum,
  rate: zero,
  rateModifiers: [],
  unitPrice: zero,
};

const initialNoun: AggregateNoun = {
  sides: [],
  parties: [],
  settlementType: [],
  ticker: [],
  cusip: [],
  eventTypes: [],
  initiators: [],
};

// only push if item is not already in array
function pushIfUnique<T>(arr: T[], item: T) {
  if (!arr.includes(item)) {
    arr.push(item);
  }
}

export function getAggHistory(historyItems: ContractHistoryEvent[], selected: 'sum' | 'avg'): Agg {
  const sum = cloneDeep(initialSum);
  const avg = cloneDeep(initialAvg);
  const noun = cloneDeep(initialNoun);

  let cntOpenQuantity = 0n;
  let sumRates = zero;

  historyItems.forEach((item: ContractHistoryEvent) => {
    if (!item.contractState) return;
    sum.openQuantity += item.contractState.openQuantity;
    sum.contractValue = sum.contractValue.add(item.contractState.contractValue);
    sum.contractValueAdjustment = sum.contractValueAdjustment.add(
      item.contractState.contractValueAdjustment
    );
    sum.interestDueAmount = sum.interestDueAmount.add(item.contractState.interestDueAmount);
    sum.unitPrice = sum.unitPrice.add(item.contractState.unitPrice);

    cntOpenQuantity++;

    pushIfUnique(noun.sides, item.contractState.side === Side.BORROW ? 'BORROW' : 'LOAN');
    // @TODO: having to manually assemble `counterpartyDisplay` agian is horrible
    // and duplicates the logic we already have in the column definition
    const companiesStore = useCompaniesStore();
    const counterparty = companiesStore.getCompany(item.contractState.counterpartyId);
    if (counterparty !== undefined) {
      pushIfUnique(noun.parties, `${counterparty.companyName} (${counterparty.displayId})`);
    }
    pushIfUnique(noun.ticker, item.contractState.instrument!.ticker);
    pushIfUnique(noun.cusip, item.contractState.instrument!.cusip);
    pushIfUnique(avg.rateModifiers, item.contractState.rateDetails?.benchmark);
    pushIfUnique(noun.eventTypes, item.eventType);
    // @TODO: we need to compare differently
    // pushIfUnique(noun.initiators, item.initiator);
    const rate = new Decimal(item.contractState.rateDetails!.rate);
    sumRates = sumRates.add(rate.mul(Number(item.contractState.openQuantity)));
  });

  avg.openQuantity = cntOpenQuantity
    ? sum.openQuantity / cntOpenQuantity +
      (sum.openQuantity % cntOpenQuantity >= cntOpenQuantity / 2n ? 1n : 0n)
    : 0n;

  avg.contractValue = cntOpenQuantity
    ? sum.contractValue.div(Number(cntOpenQuantity))
    : new Decimal(0);

  avg.contractValueAdjustment = cntOpenQuantity
    ? sum.contractValueAdjustment.div(Number(cntOpenQuantity))
    : new Decimal(0);

  avg.interestDueAmount = cntOpenQuantity
    ? sum.interestDueAmount.div(Number(cntOpenQuantity))
    : new Decimal(0);

  avg.unitPrice = cntOpenQuantity ? sum.unitPrice.div(Number(cntOpenQuantity)) : new Decimal(0);

  // NOTE: we only display the average if the `rateModifier` is the same for all selected loans
  avg.rate = sum.openQuantity ? sumRates.div(Number(sum.openQuantity)) : zero;

  return {
    sum,
    avg,
    noun,
    selected,
    nounOrQuantity: (name: string) => {
      return noun[name].length === 1 ? noun[name][0] : `(${noun[name].length})`;
    },
  };
}
